/**
 * <sarky-product-rail>
 *
 * Tabs that switch which pre-rendered collection's products are shown in
 * a product rail. Deliberately does NOT fetch anything: every tab's
 * products are already rendered server-side in Liquid at page load (see
 * sections/sarky-product-rail.liquid), so switching tabs is pure
 * show/hide - no repeat product requests, no Section Rendering API
 * involvement, no client-side product fetching or morphing of any kind.
 *
 * Implements the WAI-ARIA "tabs" pattern: role="tablist"/"tab"/
 * "tabpanel", aria-selected, aria-controls/aria-labelledby (all set in
 * the Liquid markup), and Left/Right/Home/End arrow-key navigation
 * between tabs here - when this instance renders its own tab row.
 *
 * CONTROLLED-EXTERNALLY MODE: when sections/sarky-product-rail.liquid's
 * `controlled_externally` setting is on (the homepage integration with
 * the hero tabs), this element renders NO internal tab row at all -
 * `this.tabs` is simply empty, so the click/keydown wiring below is
 * skipped, but the panels themselves (and this element's public
 * `activateByCollectionHandle` method) are still fully set up. See
 * assets/sarky-hero-tabs.js and snippets/sarky-hero-search-tabs.liquid
 * for the external controller that calls that method.
 */
class SarkyProductRail extends HTMLElement {
  connectedCallback() {
    this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));
    this.panels = Array.from(this.querySelectorAll('[role="tabpanel"]'));
    if (!this.panels.length) return;

    if (this.tabs.length) {
      this.handleClick = (event) => {
        const tab = event.currentTarget;
        this.activate(tab);
      };

      this.handleKeydown = (event) => {
        const currentIndex = this.tabs.indexOf(event.currentTarget);
        if (currentIndex === -1) return;

        let nextIndex = null;
        if (event.key === 'ArrowRight') nextIndex = (currentIndex + 1) % this.tabs.length;
        else if (event.key === 'ArrowLeft') nextIndex = (currentIndex - 1 + this.tabs.length) % this.tabs.length;
        else if (event.key === 'Home') nextIndex = 0;
        else if (event.key === 'End') nextIndex = this.tabs.length - 1;

        if (nextIndex === null) return;
        event.preventDefault();
        const nextTab = this.tabs[nextIndex];
        nextTab.focus();
        this.activate(nextTab);
      };

      this.tabs.forEach((tab) => {
        tab.addEventListener('click', this.handleClick);
        tab.addEventListener('keydown', this.handleKeydown);
      });
    }
  }

  disconnectedCallback() {
    if (!this.tabs) return;
    this.tabs.forEach((tab) => {
      tab.removeEventListener('click', this.handleClick);
      tab.removeEventListener('keydown', this.handleKeydown);
    });
  }

  activate(tab) {
    const targetId = tab.getAttribute('aria-controls');
    if (!targetId) return;

    this.tabs.forEach((t) => {
      const isTarget = t === tab;
      t.setAttribute('aria-selected', String(isTarget));
      t.tabIndex = isTarget ? 0 : -1;
    });

    this.showPanelById(targetId);
  }

  /**
   * Public entry point for an external controller (the hero tabs). Finds
   * the panel whose `data-collection` matches and shows it - independent
   * of this element's own (possibly empty, in controlled-externally
   * mode) internal tab list.
   */
  activateByCollectionHandle(handle) {
    const panel = this.panels.find((p) => p.dataset.collection === handle);
    if (panel) this.showPanelById(panel.id);
  }

  showPanelById(targetId) {
    // Reset the target panel's horizontal swipe position on every
    // switch while it's still hidden, so re-entering a tab always
    // starts at the first card instead of resuming a stale scroll
    // position from an earlier visit - done before `hidden` is cleared
    // below so there is no visible jump. Single-row-carousel panels
    // (assets/sarky-product-rail-carousel.js) also track their own
    // active index, isAnimating state and any pending queued step -
    // `reset()` clears all of that in sync with the scroll position,
    // not just the scroll position alone.
    const targetPanel = this.panels.find((panel) => panel.id === targetId);
    const carousel = targetPanel && targetPanel.querySelector('sarky-rail-carousel');
    if (carousel && typeof carousel.reset === 'function') {
      carousel.reset();
    } else {
      const rail = targetPanel && targetPanel.querySelector('.sarky-product-rail__grid');
      if (rail) rail.scrollLeft = 0;
    }

    this.panels.forEach((panel) => {
      panel.hidden = panel.id !== targetId;
    });
  }
}

if (!customElements.get('sarky-product-rail')) {
  customElements.define('sarky-product-rail', SarkyProductRail);
}
